import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Ship,
  DollarSign,
  Mail,
  MapPin,
  Package,
  User,
  Tag,
  FileText,
  Anchor,
  AlertTriangle,
  Warehouse,
  Layers,
  PlayCircle,
  ClipboardList,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ESTADO_BADGE } from '../utils/lclBoardConstants';
import {
  shortId,
  formatDate,
  formatCliente,
  formatClienteNit,
} from '../utils/lclFormatters';
import LclCotizacionesProveedoresSection from '../cotizaciones/components/LclCotizacionesProveedoresSection.jsx';
import LclOfertasSection from '../ofertas/components/LclOfertasSection.jsx';

/**
 * Detalle de una solicitud LCL.
 *
 * Gemelo de `pages/air-requests/components/AirRequestDetailDialog.jsx` (aéreo),
 * con la información marítima: puertos con UN/LOCODE, tipo de
 * servicio, CBM y unidades W/M, tipo de bulto e IMO por línea.
 *
 * Monta la sección de cotizaciones de proveedores, que trae sus
 * propios hooks: acá no se puede tener estado porque el `return null`
 * de abajo corta antes de cualquier hook.
 *
 * No monta la trazabilidad: esa vive en su propio modal. Tampoco las
 * instrucciones de embarque: las abre quien monta el detalle, vía
 * `onAbrirInstrucciones`, para poder recargar su lista al iniciar la
 * operación.
 */
export default function LclRequestDetailDialog({
  request,
  open,
  onOpenChange,
  onChanged,
  onAbrirInstrucciones,
}) {
  if (!request) return null;
  const r = request;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[80vw] max-w-none max-h-[90vh] p-0 overflow-hidden flex flex-col">
        <DialogHeader className="px-6 py-4 border-b bg-muted/30 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-2 rounded-lg">
                <Ship className="h-5 w-5 text-primary" />
              </div>
              <div className="space-y-0.5">
                <DialogTitle className="text-lg">
                  {r.codigo || shortId(r._id)}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  Creada el {formatDate(r.createdAt)} · {r.tipoOperacion} ·{' '}
                  {r.tipoServicio || '—'}
                </DialogDescription>
              </div>
            </div>
            {/* `pr-8`: deja libre la X de cerrar, que Radix pone en la esquina. */}
            <div className="flex items-center gap-2 pr-8">
              {/* Sólo si quien monta el detalle sabe abrir las instrucciones. */}
              {onAbrirInstrucciones && (r.estado === 'Borrador' || r.estado === 'Enviada') && (
                <Button
                  size="sm"
                  variant={r.estado === 'Borrador' ? 'default' : 'outline'}
                  onClick={() => onAbrirInstrucciones(r)}
                  title="Instrucciones de embarque: se notifican al proveedor de la cotización seleccionada"
                >
                  {r.estado === 'Borrador' ? (
                    <>
                      <PlayCircle className="mr-1.5 h-4 w-4" /> Iniciar operación
                    </>
                  ) : (
                    <>
                      <ClipboardList className="mr-1.5 h-4 w-4" /> Instrucciones de embarque
                    </>
                  )}
                </Button>
              )}
              {r.requiereOTM ? (
                <Badge
                  variant="outline"
                  className="font-normal border border-indigo-300 text-indigo-700 flex items-center gap-1"
                  title="El cliente requiere Operador de Transporte Multimodal"
                >
                  <Layers className="h-3 w-3" />
                  OTM
                </Badge>
              ) : null}
              <Badge
                variant="outline"
                className={cn('font-normal border', ESTADO_BADGE[r.estado] || '')}
              >
                {r.estado}
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
          <DetailSection icon={User} label="Cliente y asesor">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailKV
                label="Cliente"
                value={formatCliente(r)}
                sub={formatClienteNit(r) ? `NIT ${formatClienteNit(r)}` : null}
              />
              <DetailKV
                label="Asesor comercial"
                value={
                  r.asesorSnapshot?.nombre ||
                  (r.asesorId ? shortId(r.asesorId) : 'No asignado')
                }
                sub={r.asesorSnapshot?.correo || null}
                icon={Mail}
              />
              <DetailKV
                label="Incoterm"
                value={
                  r.incotermSnapshot
                    ? `${r.incotermSnapshot.codigo} — ${r.incotermSnapshot.nombre}`
                    : shortId(r.incotermId)
                }
                sub={r.incotermSnapshot?.version || null}
                icon={Tag}
              />
              <DetailKV
                label="Cliente (correo / teléfono)"
                value={r.clienteSnapshot?.correo || '—'}
                sub={r.clienteSnapshot?.telefono || null}
                icon={Mail}
              />
              {r.valorMercancia != null ? (
                <DetailKV
                  label="Valor de la mercancía"
                  value={`${Number(r.valorMercancia).toLocaleString('es-CO')} ${
                    r.monedaValorMercancia || 'USD'
                  }`}
                  sub="Declarado por el cliente (aproximado)"
                  icon={Tag}
                />
              ) : null}
            </div>
          </DetailSection>

          <DetailSection icon={MapPin} label="Ruta marítima">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <SeaportCard
                title="Puerto de carga (origen)"
                snap={r.puertoCargaSnapshot}
                pais={r.paisOrigen}
                ciudadPuerta={r.ciudadOrigen}
                ciudadPuertaLabel="Recolección en"
                fallbackId={r.puertoCargaId}
              />
              <SeaportCard
                title="Puerto de descarga (destino)"
                snap={r.puertoDescargaSnapshot}
                pais={r.paisDestino}
                ciudadPuerta={r.ciudadDestino}
                ciudadPuertaLabel="Entrega en"
                fallbackId={r.puertoDescargaId}
              />
            </div>
            {r.direccionRecogidaOrigen ? (
              <div className="mt-3 rounded-md border border-amber-300 bg-amber-50/60 dark:bg-amber-900/10 p-3">
                <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                  <Warehouse className="h-3 w-3" />
                  Dirección de recogida en origen
                </div>
                <div className="text-sm text-foreground mt-0.5 break-words">
                  {r.direccionRecogidaOrigen}
                </div>
              </div>
            ) : null}
          </DetailSection>

          <DetailSection icon={Package} label="Carga">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Bultos" value={r.numPiezas} />
              <Stat label="Peso total" value={`${Number(r.pesoTotal || 0).toFixed(2)} kg`} />
              <Stat label="Volumen (CBM)" value={`${Number(r.volumenTotal || 0).toFixed(3)} m³`} />
              <Stat label="Toneladas" value={Number(r.toneladasTotal || 0).toFixed(3)} />
              <Stat
                label="Unidades W/M"
                value={Number(r.unidadesWM || 0).toFixed(3)}
                highlight
              />
            </div>
          </DetailSection>

          {r.descripcionResumen ? (
            <DetailSection icon={FileText} label="Descripción">
              <p className="text-sm text-foreground whitespace-pre-wrap">
                {r.descripcionResumen}
              </p>
            </DetailSection>
          ) : null}

          {Array.isArray(r.detalles) && r.detalles.length > 0 ? (
            <DetailSection icon={Package} label="Detalle de bultos">
              <div className="rounded-md border overflow-x-auto">
                <div className="hidden sm:grid grid-cols-[0.7fr_0.9fr_1fr_1fr_1fr_1fr_0.9fr_1.4fr] gap-1 px-3 py-2 bg-muted/30 text-[10px] font-semibold text-muted-foreground uppercase tracking-wide min-w-[820px]">
                  <span>Bultos</span>
                  <span>Tipo</span>
                  <span>Largo</span>
                  <span>Ancho</span>
                  <span>Alto</span>
                  <span>Peso x unidad</span>
                  <span>m³</span>
                  <span>Mercancía / Apilable</span>
                </div>
                <div className="divide-y min-w-[820px]">
                  {r.detalles.map((d, i) => (
                    <div
                      key={d._id || i}
                      className="grid grid-cols-2 sm:grid-cols-[0.7fr_0.9fr_1fr_1fr_1fr_1fr_0.9fr_1.4fr] gap-1 px-3 py-2 text-sm hover:bg-muted/10"
                    >
                      <span className="font-mono">{d.numPiezas ?? '—'}</span>
                      <span className="text-xs text-muted-foreground">
                        {d.tipoBulto || '—'}
                      </span>
                      <span className="font-mono">
                        {d.largo ?? '—'} {d.unidadDimension || ''}
                      </span>
                      <span className="font-mono">
                        {d.ancho ?? '—'} {d.unidadDimension || ''}
                      </span>
                      <span className="font-mono">
                        {d.alto ?? '—'} {d.unidadDimension || ''}
                      </span>
                      <span className="font-mono">
                        {d.peso ?? '—'} {d.unidadPeso || ''}
                      </span>
                      <span className="font-mono">
                        {Number(d.volumen || 0).toFixed(3)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {d.tipoMercanciaSnapshot?.nombre ||
                          d.tipoMercanciaSnapshot?.descripcion ||
                          'Sin tipo'}{' '}
                        · {d.apilable || '—'}
                        {d.imo ? (
                          <span className="ml-1 inline-flex items-center gap-0.5 text-amber-700">
                            <AlertTriangle className="h-3 w-3" />
                            IMO {d.imoClase || ''}
                            {d.unNumber ? ` · ${d.unNumber}` : ''}
                          </span>
                        ) : null}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </DetailSection>
          ) : null}

          <DetailSection icon={DollarSign} label="Cotizaciones de proveedores">
            <LclCotizacionesProveedoresSection
              lclRequestId={r._id}
              lclRequest={r}
              onChanged={onChanged}
            />
          </DetailSection>

          <DetailSection icon={FileText} label="Oferta al cliente">
            <LclOfertasSection
              lclRequestId={r._id}
              lclRequest={r}
              onChanged={onChanged}
            />
          </DetailSection>

          {/* La trazabilidad NO se monta acá: tiene su propio modal,
              que se abre con la acción "Trazabilidad" de la tarjeta o
              del tablero. Este diálogo muestra solo el detalle de la
              operación. */}

          <div className="text-[10px] text-muted-foreground font-mono pt-2 border-t">
            <div>ID interno: {r._id}</div>
            {r.clienteId ? <div>clienteId: {r.clienteId}</div> : null}
            {r.asesorId ? <div>asesorId: {r.asesorId}</div> : null}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailSection({ icon: Icon, label, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10 text-primary">
          <Icon size={13} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {label}
        </span>
        <div className="flex-1 h-px bg-border" />
      </div>
      {children}
    </section>
  );
}

function DetailKV({ label, value, sub, icon: Icon }) {
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        {Icon ? <Icon className="h-3 w-3" /> : null}
        {label}
      </div>
      <div className="text-sm text-foreground break-words">{value || '—'}</div>
      {sub ? <div className="text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2',
        highlight ? 'bg-primary/5 border-primary/30' : 'bg-muted/20',
      )}
    >
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-base font-semibold text-foreground mt-0.5">{value}</div>
    </div>
  );
}

function SeaportCard({ title, snap, pais, ciudadPuerta, ciudadPuertaLabel, fallbackId }) {
  return (
    <div className="rounded-md border bg-muted/20 p-3 space-y-1">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        <Anchor className="h-3 w-3" />
        {title}
      </div>
      {snap?.nombre ? (
        <>
          <div className="text-sm font-medium text-foreground">
            {snap.nombre}{' '}
            {snap.unlocode ? (
              <span className="text-xs font-mono text-muted-foreground">
                ({snap.unlocode})
              </span>
            ) : null}
          </div>
          <div className="text-xs text-muted-foreground">
            {[snap.ciudad, snap.pais].filter(Boolean).join(', ')}
            {snap.tipoTerminal ? ` · ${snap.tipoTerminal}` : ''}
          </div>
        </>
      ) : (
        <>
          <div className="text-sm font-medium text-foreground">
            {pais || shortId(fallbackId)}
          </div>
          <div className="text-[10px] font-mono text-muted-foreground">{fallbackId}</div>
        </>
      )}
      {ciudadPuerta ? (
        <div className="text-xs text-primary pt-1">
          {ciudadPuertaLabel}: <strong>{ciudadPuerta}</strong>
        </div>
      ) : null}
    </div>
  );
}
